export interface ModuleDeliverable {
  title: string;
}

export interface Module {
  id: number;
  title: string;
  description?: string;
  fullDescription?: string;
  transformation?: string;
  skills?: string[];
  deliverables?: ModuleDeliverable[];
  tech?: string[];
}

export interface Academy {
  id: string;
  name: string;
  tagline?: string;
  description: string;
  modules: Module[];
}

export const academiesData: Academy[] = [
  {
    id: "faceless-content",
    name: "Faceless Content Systems",
    tagline: "The Invisible Virality Machine",
    description: "How to create viral, automated, and profitable content without showing your face.",
    modules: [
      {
        id: 1,
        title: "The Invisible Virality Code",
        transformation: "Teach students to understand how algorithms and virality work without ever appearing on video.",
        fullDescription: "Students learn how the viral cycle works: retention, watch time, CTR, repetition, sound design, and short-form storytelling adapted to faceless formats. Includes analysis of million-follower faceless accounts and reverse engineering.",
        skills: [
          "Understand viral triggers",
          "Understand anonymous human behavior",
          "Create invisible attention hooks"
        ],
        deliverables: [
          { title: "Guide: The 32 Faceless Virality Triggers" },
          { title: "10 Ready-to-Use Invisible Viral Video Scripts" }
        ],
        tech: ["ChatGPT", "OpusClip", "CapCut", "Pika Labs", "Runway"]
      },
      {
        id: 2,
        title: "Faceless Niche Architecture",
        transformation: "Choose viral and scalable niches that work without showing your face.",
        fullDescription: "Framework for niche analysis using AI + trends. Includes faceless micro-niches (list of 250).",
        skills: [
          "Professional niche selection",
          "CPM, demand, and competition analysis",
          "Invisible persona creation"
        ],
        deliverables: [
          { title: "Database of 250 Faceless Niches" },
          { title: "Ideal Niche Tool (Interactive Prompt)" }
        ],
        tech: ["ChatGPT", "Google Trends", "VidIQ"]
      },
      {
        id: 3,
        title: "Automated Scripts & Storytelling",
        transformation: "Create complete texts and scripts effortlessly using only AI.",
        fullDescription: "How to use AI to create 15-60s storytelling that generates insane retention. F.A.C.E. Framework = Faceless Attention Capture Engine.",
        skills: [
          "Short script construction",
          "Viral writing",
          "Ghost storytelling"
        ],
        deliverables: [
          { title: "50 Ready-to-Use Faceless Scripts" },
          { title: "AI Script Templates" },
          { title: "F.A.C.E. Framework" }
        ],
        tech: ["ChatGPT", "Claude", "Notion AI"]
      },
      {
        id: 4,
        title: "100% AI Video Production",
        transformation: "Create complete videos without filming, recording, or using your voice.",
        fullDescription: "AI for videos: avatars, animations, automatic b-roll, AI voice, automatic music.",
        skills: [
          "AI video production",
          "Quick editing for retention",
          "Faceless viral captions"
        ],
        deliverables: [
          { title: "Blueprint: Complete Video in 10 Minutes" },
          { title: "Premium Faceless Assets Pack" }
        ],
        tech: ["Runway", "Pika Labs", "ElevenLabs", "CapCut", "Pictory"]
      },
      {
        id: 5,
        title: "Faceless TikTok Mastery",
        transformation: "Master TikTok with an invisible identity.",
        fullDescription: "Virality strategies, frequency, invisible hashtags, shadow tactics, repost automation.",
        skills: [
          "TikTok Growth",
          "Anonymous account building",
          "Repeatable virality"
        ],
        deliverables: [
          { title: "30-Day Editorial Calendar" },
          { title: "20 Faceless Viral Ideas" },
          { title: "Upload Automation Script" }
        ],
        tech: ["TikTok", "CapCut", "Later", "Repurpose.io"]
      },
      {
        id: 6,
        title: "High-Retention Reels & Shorts",
        transformation: "Strategies to dominate Reels and Shorts without appearing.",
        fullDescription: "How to adapt TikTok content for Reels/Shorts with platform-specific retention adjustments.",
        skills: [
          "Multi-platform distribution",
          "Advanced retention",
          "Platform optimization"
        ],
        deliverables: [
          { title: "10/10 Retention Checklist" },
          { title: "15 Faceless Thumbnail Templates" }
        ],
        tech: ["Instagram", "YouTube", "CapCut", "Canva"]
      },
      {
        id: 7,
        title: "YouTube Phantom Automation",
        transformation: "Create entire channels without voice, face, or recording.",
        fullDescription: "AI for voice, video, editing, and automatic channel structure. Includes case studies of channels making $50k-$100k/month without appearing.",
        skills: [
          "Operate AI channel",
          "Automate uploads",
          "Create high-CTR thumbnails"
        ],
        deliverables: [
          { title: "AI Channel in 48h System" },
          { title: "10 Ready Scripts (MrBeast Faceless Style)" }
        ],
        tech: ["YouTube Studio", "ElevenLabs", "Pictory", "TubeBuddy"]
      },
      {
        id: 8,
        title: "SEO for Faceless Content",
        transformation: "Make videos grow organically via search and trends.",
        fullDescription: "Invisible optimization, keyword clustering, retention and recommendation strategies.",
        skills: [
          "Modern SEO",
          "Organic growth",
          "Data analysis"
        ],
        deliverables: [
          { title: "Ghost SEO Guide" },
          { title: "AI Tracking Spreadsheet" }
        ],
        tech: ["VidIQ", "TubeBuddy", "Ahrefs", "Google Trends"]
      },
      {
        id: 9,
        title: "Invisible Express Monetization",
        transformation: "Transform any faceless account into an income source.",
        fullDescription: "Invisible affiliates, sponsored content without appearing, hidden links, faceless digital products.",
        skills: [
          "Monetization",
          "Asset creation",
          "Income optimization"
        ],
        deliverables: [
          { title: "50 Faceless Affiliate Offers" },
          { title: "Invisible Sales Page Template" }
        ],
        tech: ["ClickBank", "Digistore24", "Gumroad", "Stan Store"]
      },
      {
        id: 10,
        title: "Multi-Channel Scale System",
        transformation: "Scale to 3-10 faceless accounts with total automation.",
        fullDescription: "How to create an entire network of invisible accounts operating on autopilot.",
        skills: [
          "Total automation",
          "Network management",
          "Invisible delegation"
        ],
        deliverables: [
          { title: "Complete Scale SOP" },
          { title: "Multi-Account Automation Script" }
        ],
        tech: ["Zapier", "Make", "Notion", "Slack"]
      }
    ],
  },
  {
    id: "phantom-websites",
    name: "Phantom Websites & Blogs Automation",
    tagline: "The Invisible Passive Traffic & Automated Income Machine",
    description: "Here you create an entire network of invisible blogs that rank on Google, generate 24/7 traffic, and monetize with ads, affiliates, and products. All with AI and automation.",
    modules: [
      {
        id: 1,
        title: "Ghost Blog Structure",
        transformation: "Create a professional anonymous blog in less than 24 hours.",
        fullDescription: "Students learn: ideal structure, essential pages, content architecture, how to maintain total anonymity, and how to build invisible authority.",
        skills: [
          "Build blogs professionally",
          "Create hidden blog identity",
          "Configure essential systems"
        ],
        deliverables: [
          { title: "Premium Blog Template (WordPress + AI)" },
          { title: "Invisible Blog Ready Checklist" }
        ],
        tech: ["WordPress", "Notion", "Cloudflare", "RankMath"]
      },
      {
        id: 2,
        title: "Profitable Niches for Ghost Blogs",
        transformation: "Choose with precision niches that make money and have low competition.",
        fullDescription: "Deep analysis of: evergreen niches, hidden micro-niches, Google Discover content, high-ticket affiliate niches.",
        skills: [
          "Professional niche selection",
          "Competition and demand analysis",
          "Invisible editorial identity"
        ],
        deliverables: [
          { title: "List of 300 Hidden Niches" },
          { title: "AI Tool: Find Your Ideal Niche" }
        ],
        tech: ["Ahrefs", "SEMrush", "Google Trends", "ChatGPT"]
      },
      {
        id: 3,
        title: "High-Quality AI Articles (S.I.A System)",
        transformation: "Create perfect articles for Google using AI with real depth.",
        fullDescription: "Teaching the S.I.A System — Semantic IA Architecture, which builds articles: deep, plagiarism-free, 100% optimized, 3,000-5,000 words, invisible human style.",
        skills: [
          "AI writing mastery",
          "Advanced semantic structure",
          "Unlimited article production"
        ],
        deliverables: [
          { title: "Complete S.I.A Template" },
          { title: "10 Optimized Model Articles" }
        ],
        tech: ["ChatGPT", "Claude", "Surfer SEO", "Frase"]
      },
      {
        id: 4,
        title: "Ghost SEO: Modern Techniques 2024-2025",
        transformation: "Rank content without backlinks and without exposure.",
        fullDescription: "Learning: cluster-based SEO, invisible interlinking, topical authority, search intent, AI semantic entities.",
        skills: [
          "Modern SEO",
          "Invisible authority building",
          "Metrics and positioning analysis"
        ],
        deliverables: [
          { title: "Invisible SEO Guide" },
          { title: "AI Keyword Research Tool" }
        ],
        tech: ["Ahrefs", "SEMrush", "RankMath", "Clearscope"]
      },
      {
        id: 5,
        title: "Google Discover Domination",
        transformation: "Learn to dominate explosive Discover traffic — the factor that generates 100k+ visits with a single article.",
        fullDescription: "Hot topics, ideal Discover structure, high-CTR AI images, invisible headlines, anti-penalization techniques.",
        skills: [
          "Create content for viral organic traffic",
          "Explore trends with AI",
          "Optimize thumbnails and titles"
        ],
        deliverables: [
          { title: "50 Perfect Headlines for Discover" },
          { title: "Discover Approval Checklist" }
        ],
        tech: ["Google Search Console", "Canva", "Midjourney", "ChatGPT"]
      },
      {
        id: 6,
        title: "Ghost Monetization (Ads, Affiliates, Hidden Products)",
        transformation: "Create multiple invisible income sources.",
        fullDescription: "How to make money with: AdSense, Ezoic/Raptive, invisible affiliates, hidden digital products, print-on-demand integrated with blog, invisible landing pages.",
        skills: [
          "Income optimization",
          "Multi-niche strategies",
          "Building monetary assets"
        ],
        deliverables: [
          { title: "100 Affiliate Programs" },
          { title: "20 Invisible Digital Product Ideas" }
        ],
        tech: ["Google AdSense", "Ezoic", "ShareASale", "Gumroad"]
      },
      {
        id: 7,
        title: "Complete Content Automation",
        transformation: "Make the blog run by itself.",
        fullDescription: "Automation for: weekly creation, publishing, automatic interlinking, distribution to faceless networks, integration with hidden newsletters.",
        skills: [
          "Build automatic pipelines",
          "AI planning",
          "Multi-platform integration"
        ],
        deliverables: [
          { title: "Blog on Autopilot Blueprint" },
          { title: "AI Scripts for Editorial Calendar" }
        ],
        tech: ["Zapier", "Make", "WordPress", "Buffer"]
      },
      {
        id: 8,
        title: "Invisible Authority Portals",
        transformation: "Build niche sites that look massive — but are operated only by the student.",
        fullDescription: "How to create: multi-niche portals, massive clusters, authority pages, invisible expansion systems.",
        skills: [
          "Authority structure",
          "Advanced SEO",
          "Portal scaling"
        ],
        deliverables: [
          { title: "Premium Portal Template" },
          { title: "50-Page Authority Structure" }
        ],
        tech: ["WordPress", "Elementor", "RankMath", "Ahrefs"]
      },
      {
        id: 9,
        title: "Create 5-10 Automated Blogs",
        transformation: "Scale a network of blogs that generates exponential traffic.",
        fullDescription: "Portfolio management: multiple niches, multiple domains, multiple income streams, invisible delegation.",
        skills: [
          "Digital portfolio",
          "Scale management",
          "Systems integration"
        ],
        deliverables: [
          { title: "Multi-Blog Management Spreadsheet" },
          { title: "Quick Creation SOP (2h per blog)" }
        ],
        tech: ["Notion", "Airtable", "Cloudflare", "Namecheap"]
      },
      {
        id: 10,
        title: "Ghost Traffic & Automatic Growth",
        transformation: "Increase traffic naturally without appearing and without ads.",
        fullDescription: "SEO + Discover combined, content reuse, invisible links, AI propagation strategies.",
        skills: [
          "Advanced organic traffic",
          "AI expansion",
          "Invisible growth"
        ],
        deliverables: [
          { title: "Automatic Growth Map" },
          { title: "24/7 Traffic Checklist" }
        ],
        tech: ["Google Analytics", "Search Console", "Ahrefs", "ChatGPT"]
      }
    ],
  },
  {
    id: "invisible-ecom",
    name: "Invisible E-Com & Digital Products",
    tagline: "Stores That Sell Themselves. Products That Don't Reveal Who You Are.",
    description: "A complete academy to create and scale faceless stores and digital products using AI, automation, and invisible selling strategies.",
    modules: [
      {
        id: 1,
        title: "Invisible Store Architecture",
        transformation: "Create a faceless store with no personal identity and hidden branding.",
        fullDescription: "Students learn: how to create a completely anonymous e-commerce presence, how to structure layout, trust, invisible social proof, how to operate a clean and highly converting store.",
        skills: [
          "Minimalist faceless design",
          "High-conversion store structure",
          "Invisible branding"
        ],
        deliverables: [
          { title: "Invisible Store Template (Shopify/WooCommerce)" },
          { title: "Perfect Invisible Store Checklist" }
        ],
        tech: ["Shopify", "WooCommerce", "Canva", "Figma"]
      },
      {
        id: 2,
        title: "Profitable Niches for Faceless E-Commerce",
        transformation: "Choose niches that work without appearing and accept hidden identities.",
        fullDescription: "Includes: 'founderless' products, products without need for personal storytelling, AI trend analysis, niches for POD, dropshipping, PLR and digital.",
        skills: [
          "Scalable niche selection",
          "Invisible persona building",
          "Quick viability analysis"
        ],
        deliverables: [
          { title: "List of 200 Faceless Niches" },
          { title: "AI E-Commerce Niche Selection Tool" }
        ],
        tech: ["Google Trends", "Jungle Scout", "ChatGPT", "AliExpress"]
      },
      {
        id: 3,
        title: "Print-on-Demand with Advanced AI",
        transformation: "Create physical products without ever showing your face and with 100% AI designs.",
        fullDescription: "Learn to create: t-shirts, posters, covers, premium art, entire automated collections. All without identity revelation, using AI for branding.",
        skills: [
          "Professional AI creation",
          "Commercial design",
          "Faceless collections"
        ],
        deliverables: [
          { title: "100 Ready AI Prompts for Collections" },
          { title: "Faceless Product Sheet Template" }
        ],
        tech: ["Printful", "Printify", "Midjourney", "DALL-E"]
      },
      {
        id: 4,
        title: "High-Conversion Ghost Catalog",
        transformation: "Create an invisible product catalog that converts 2-4× more.",
        fullDescription: "Includes: optimized product pages, high-conversion AI descriptions, AI-generated images, invisible bundles, silent upsells.",
        skills: [
          "Invisible conversion copy",
          "Page optimization",
          "Upsell structures"
        ],
        deliverables: [
          { title: "20 Product Page Templates" },
          { title: "Premium Faceless Image Pack" }
        ],
        tech: ["Shopify", "Canva", "ChatGPT", "Hotjar"]
      },
      {
        id: 5,
        title: "Invisible Digital Products",
        transformation: "Create and sell digital products without authorship and without exposure.",
        fullDescription: "Teaches: invisible e-books, planners, templates, faceless courses, prompt packs, AI mini-solutions.",
        skills: [
          "Strategic digital creation",
          "PLR/AI monetization",
          "Professional digital design"
        ],
        deliverables: [
          { title: "50 Faceless Digital Product Ideas" },
          { title: "Editable Templates for Quick Creation" }
        ],
        tech: ["Canva", "Notion", "Gumroad", "Stan Store"]
      },
      {
        id: 6,
        title: "Depersonalized Sales Funnels",
        transformation: "Create invisible funnels without personal video and without human storytelling.",
        fullDescription: "Includes: 1-page funnel, automatic multi-layer funnel, funnel without personal video, hybrid funnels for physical/digital products.",
        skills: [
          "Professional funnel building",
          "Anonymous copy",
          "Automation"
        ],
        deliverables: [
          { title: "10 Ready Invisible Funnels" },
          { title: "Complete Faceless Sales Page Copy" }
        ],
        tech: ["ClickFunnels", "Systeme.io", "Carrd", "Mailchimp"]
      },
      {
        id: 7,
        title: "Faceless Traffic for E-Commerce",
        transformation: "Attract traffic without personal branding and without exposure.",
        fullDescription: "Learn: invisible TikTok Ads, hidden Native Ads, SEO for products, cold traffic to faceless pages.",
        skills: [
          "Invisible traffic mastery",
          "Faceless ad creation",
          "CPA optimization"
        ],
        deliverables: [
          { title: "30 Ready Faceless Creatives" },
          { title: "AI Scripts for Ad Headlines" }
        ],
        tech: ["TikTok Ads", "Meta Ads", "Taboola", "Google Ads"]
      },
      {
        id: 8,
        title: "Faceless Launches (Physical & Digital)",
        transformation: "Learn to launch products without appearing in videos, lives, or stories.",
        fullDescription: "Includes: 7× invisible launch, faceless seed launch, automatic continuous launch, invisible hype strategies.",
        skills: [
          "Campaign management",
          "Anonymous launch copy",
          "Invisible buzz creation"
        ],
        deliverables: [
          { title: "Invisible Launch Calendar" },
          { title: "20 AI Email Sequences" }
        ],
        tech: ["ConvertKit", "Mailchimp", "Notion", "Zapier"]
      },
      {
        id: 9,
        title: "Advanced Hidden Monetization",
        transformation: "Learn to extract maximum profit from invisible e-commerce.",
        fullDescription: "Advanced tactics: invisible bundles, hidden margins, faceless upsell, AI downsell, hybrid sales (physical + digital).",
        skills: [
          "Profit optimization",
          "Invisible cross-selling",
          "Strategic pricing"
        ],
        deliverables: [
          { title: "12 Faceless Pricing Models" },
          { title: "Margins and Profits Spreadsheet" }
        ],
        tech: ["Shopify", "Google Sheets", "ChatGPT", "Stripe"]
      },
      {
        id: 10,
        title: "Scale 3-5 Ghost Stores",
        transformation: "Operate multiple invisible stores at the same time using automation.",
        fullDescription: "Includes: replication systems, duplicate stores, ghost team for e-com, total operations automation.",
        skills: [
          "Massive scaling",
          "Multi-store management",
          "Advanced automation"
        ],
        deliverables: [
          { title: "Complete SOP for Invisible Management" },
          { title: "E-Commerce on Autopilot Blueprint" }
        ],
        tech: ["Shopify", "Notion", "Slack", "Zapier"]
      }
    ],
  },
  {
    id: "ghost-newsletters",
    name: "Ghost Newsletters & Subscription Empires",
    tagline: "The Art of Creating Profitable Subscriptions Without Face, Voice, or Exposure",
    description: "In this academy, students learn to build anonymous, highly profitable, completely automated newsletters that can potentially be sold as premium digital assets.",
    modules: [
      {
        id: 1,
        title: "Hidden Newsletter Fundamentals",
        transformation: "Understand how an anonymous newsletter works and why it's one of the most profitable models without exposure.",
        fullDescription: "Students learn: architecture of an invisible newsletter, anonymous editorial identity, content formats, creating a 'ghost voice' using AI, how to avoid identity signals.",
        skills: [
          "Invisible editorial creation",
          "Newsletter structuring",
          "Digital identity control"
        ],
        deliverables: [
          { title: "Editorial Structure Template" },
          { title: "Invisible Newsletter in 24h Blueprint" }
        ],
        tech: ["Substack", "Ghost", "Beehiiv", "ConvertKit"]
      },
      {
        id: 2,
        title: "Profitable Niches for Ghost Subscriptions",
        transformation: "Select highly profitable and recurring niches for newsletters.",
        fullDescription: "Includes: finance, AI & tech, productivity, geopolitics, health & performance, underexplored micro-vertical niches, invisible 'news + opinion' newsletters.",
        skills: [
          "Niche analysis",
          "Quick validation",
          "Thematic architecture"
        ],
        deliverables: [
          { title: "List of 300 Hidden Niches" },
          { title: "AI Tool: Perfect Niche for Subscription" }
        ],
        tech: ["SparkToro", "Google Trends", "ChatGPT", "Substack"]
      },
      {
        id: 3,
        title: "Professional AI Ghostwriting",
        transformation: "Create premium, engaging, and highly consistent texts without appearing AI-written.",
        fullDescription: "Presentation of techniques: AI editorial persona, invisible style/tone/cadence, S.C.R.I.P.T. framework for newsletters, simulated human ghostwriting.",
        skills: [
          "Premium writing",
          "Invisible tone creation",
          "Unlimited AI production"
        ],
        deliverables: [
          { title: "20 Newsletter Text Templates" },
          { title: "AI Script: Write Like Me Without Me Existing" }
        ],
        tech: ["ChatGPT", "Claude", "Jasper", "Notion AI"]
      },
      {
        id: 4,
        title: "Premium Content Structure",
        transformation: "Create newsletters that people actually want to open and pay for.",
        fullDescription: "Formats of: deep dives, analyses, trends, invisible opinion, AI data and insights, premium lists, visual frameworks.",
        skills: [
          "Editorial architecture",
          "Silent copywriting",
          "Value content building"
        ],
        deliverables: [
          { title: "Premium Newsletter Template" },
          { title: "15 Content Frameworks" }
        ],
        tech: ["Notion", "Figma", "Canva", "Beehiiv"]
      },
      {
        id: 5,
        title: "Invisible Organic Growth",
        transformation: "Build audience without using personal social media.",
        fullDescription: "Strategies: SEO + newsletters, hidden communities, faceless platforms, invisible Twitter, ghost guest posts, discrete cross-promotions.",
        skills: [
          "Invisible growth",
          "Lead acquisition",
          "Anonymous marketing"
        ],
        deliverables: [
          { title: "20 Scripts for Invisible Outreach" },
          { title: "Ghost Organic Growth Guide" }
        ],
        tech: ["Twitter/X", "Reddit", "Medium", "LinkedIn"]
      },
      {
        id: 6,
        title: "Growth Hacking for Faceless Newsletters",
        transformation: "Accelerate growth using automation, AI, and psychology.",
        fullDescription: "Includes: invisible referral loops, faceless gamification, hidden incentives, AI for automated growth.",
        skills: [
          "Advanced growth",
          "Acquisition automation",
          "Behavioral strategy"
        ],
        deliverables: [
          { title: "15 Referral Automations" },
          { title: "Invisible Landing Page Template" }
        ],
        tech: ["SparkLoop", "Viral Loops", "Zapier", "Make"]
      },
      {
        id: 7,
        title: "Monetization (Substack, Ghost, Beehiiv)",
        transformation: "Transform newsletters into recurring income sources.",
        fullDescription: "Includes: premium subscriptions, intelligent paywall, invisible sponsorships, hidden affiliates, digital bundles.",
        skills: [
          "Recurring monetization",
          "Paywall management",
          "Invisible negotiation"
        ],
        deliverables: [
          { title: "20 Paywall Models" },
          { title: "50 Potential Sponsors" }
        ],
        tech: ["Stripe", "Substack", "Ghost", "Beehiiv"]
      },
      {
        id: 8,
        title: "Invisible Sponsorships",
        transformation: "Sell advertising space without exposure, voice, or personal interaction.",
        fullDescription: "Includes: how to negotiate anonymously, create invisible sales cases, where to find advertisers, pricing for small and large newsletters.",
        skills: [
          "Silent negotiation",
          "Professional proposals",
          "Sponsor management"
        ],
        deliverables: [
          { title: "Invisible Media Kit Template" },
          { title: "10 Sponsorship Proposal Models" }
        ],
        tech: ["Passionfroot", "Swapstack", "Notion", "Google Docs"]
      },
      {
        id: 9,
        title: "Create 2-3 Newsletters Simultaneously",
        transformation: "Manage multiple newsletters with AI and invisible delegation.",
        fullDescription: "Includes: multi-project management, delegation to ghostwriters, parallel AI copy, how to grow multiple niches simultaneously.",
        skills: [
          "Multi-newsletter management",
          "Invisible delegation",
          "Advanced editorial system"
        ],
        deliverables: [
          { title: "Multi-Newsletter Operation SOP" },
          { title: "Editorial Control Spreadsheet" }
        ],
        tech: ["Notion", "Airtable", "Slack", "Zapier"]
      },
      {
        id: 10,
        title: "Scale to a Subscription Empire",
        transformation: "Create a true newsletter ecosystem generating predictable income.",
        fullDescription: "Includes: hybrid models, integration with YouTube Automation, cross-conversion with ghost blogs, building sellable assets.",
        skills: [
          "Scaling",
          "Invisible multimedia integration",
          "Digital asset management"
        ],
        deliverables: [
          { title: "Subscription Empire Blueprint" },
          { title: "Annual Expansion Framework" }
        ],
        tech: ["Stripe", "Notion", "Google Analytics", "Beehiiv"]
      }
    ],
  },
  {
    id: "automated-business",
    name: "Automation Empire & Ghost Team",
    tagline: "The Definitive System to Operate 3-10 Faceless Businesses on Autopilot",
    description: "This academy transforms an ordinary student into a high-level invisible operator, with multiple businesses running through: automation, AI, intelligent delegation, invisible SOPs, ghost team, and scaling systems. The student gains a digital invisible empire, managed almost entirely without human intervention.",
    modules: [
      {
        id: 1,
        title: "The Ghost Operator: Mindset & Structure",
        transformation: "The student stops thinking like a creator and starts acting as an invisible operator.",
        fullDescription: "How to think like an invisible CEO, multi-business faceless structure, identity and digital footprint control, how to organize an empire of silent businesses.",
        skills: [
          "Strategic management",
          "Invisible decision-making",
          "Operational architecture"
        ],
        deliverables: [
          { title: "Ghost Operator Blueprint" },
          { title: "Invisible Identity Map" }
        ],
        tech: ["Notion", "Slack", "Loom", "Figma"]
      },
      {
        id: 2,
        title: "48h Business Creation System",
        transformation: "Learn to set up a new faceless business in 1-2 days.",
        fullDescription: "Includes: model selection, AI rapid creation system, instant invisible branding, minimal functional structure. Main Framework: L.I.T.E. — Launch Invisible in Two Evenings.",
        skills: [
          "Rapid creation",
          "Prototyping",
          "Accelerated execution"
        ],
        deliverables: [
          { title: "48h Launch Checklist" },
          { title: "5 Complete Business Models" }
        ],
        tech: ["ChatGPT", "Canva", "Carrd", "Notion"]
      },
      {
        id: 3,
        title: "Absolute AI Automation",
        transformation: "Automate everything possible to operate businesses without human presence.",
        fullDescription: "Learn to automate: content, uploads, emails, customers, sales, invisible support, blog/newsletter/e-com operations.",
        skills: [
          "Advanced automation",
          "AI applied to business",
          "Manual task elimination"
        ],
        deliverables: [
          { title: "30 Ready-to-Use Automations" },
          { title: "90% Automatic Business Blueprint" }
        ],
        tech: ["Zapier", "Make", "OpenAI", "PhantomBuster", "Notion AI"]
      },
      {
        id: 4,
        title: "Invisible Delegation (Ghost Team Framework)",
        transformation: "Learn to hire and manage freelancers without exposing identity.",
        fullDescription: "Includes: hiring without photo/identity, avatar management, invisible communication, creating ghost coordinator personas. Exclusive Framework: GHOST TEAM PROTOCOL.",
        skills: [
          "Professional delegation",
          "Faceless remote management",
          "Invisible SOP creation"
        ],
        deliverables: [
          { title: "10 Task Models for Ghost Team" },
          { title: "Invisible Contracts (Templates)" }
        ],
        tech: ["Upwork", "Fiverr", "Slack", "Notion"]
      },
      {
        id: 5,
        title: "Advanced SOPs for Ghost Operations",
        transformation: "Create systems that allow the business to run without human intervention.",
        fullDescription: "Includes: SOPs for faceless accounts, SOPs for blogs, SOPs for e-com, SOPs for newsletters, SOPs for automation and AI.",
        skills: [
          "Scalable documentation",
          "Invisible process creation",
          "Silent business operation"
        ],
        deliverables: [
          { title: "20 Ready-to-Copy SOPs" },
          { title: "Perfect Invisible SOP Framework" }
        ],
        tech: ["Notion", "Loom", "Scribe", "Google Docs"]
      },
      {
        id: 6,
        title: "Integrated Multiple Monetization",
        transformation: "Connect all faceless businesses into an integrated monetization network.",
        fullDescription: "Student learns: how a faceless channel leverages a blog, how the blog feeds a newsletter, how the newsletter sells invisible digital products, how everything converts to full monetization. Framework: M.I.S. — Monetization Integration System.",
        skills: [
          "Holistic monetization",
          "Hybrid strategy",
          "Invisible cross-selling"
        ],
        deliverables: [
          { title: "Integrated Monetization Map" },
          { title: "10 Hybrid Invisible Funnel Models" }
        ],
        tech: ["Stripe", "Gumroad", "ConvertKit", "Shopify"]
      },
      {
        id: 7,
        title: "Cross-Ecosystem Growth",
        transformation: "Learn to make all businesses grow using each other's traffic.",
        fullDescription: "Includes: invisible cross-traffic, distribution automation, AI content repurposing, intelligent network between businesses.",
        skills: [
          "Massive organic growth",
          "Platform integration",
          "Digital synergy strategies"
        ],
        deliverables: [
          { title: "Ghost Growth Network Blueprint" },
          { title: "AI Automatic Distribution Tool" }
        ],
        tech: ["Buffer", "Repurpose.io", "Zapier", "Make"]
      },
      {
        id: 8,
        title: "Scaling the Business Ecosystem",
        transformation: "Create an ecosystem of 5-10 businesses operating together on autopilot.",
        fullDescription: "Includes: digital empire architecture, invisible dashboard management, complete multi-business automation, how to expand systematically.",
        skills: [
          "Systems thinking",
          "Network operation",
          "Silent business scaling"
        ],
        deliverables: [
          { title: "Invisible Management Dashboard" },
          { title: "Annual Expansion Template" }
        ],
        tech: ["Notion", "Airtable", "Google Analytics", "Slack"]
      },
      {
        id: 9,
        title: "Advanced Invisible Identity",
        transformation: "Create a completely inaccessible digital persona, impossible to track.",
        fullDescription: "Includes: invisible footprint, ghost navigation, creating multiple digital identities, protection against tracking and exposure.",
        skills: [
          "Cyber invisibility",
          "Digital protection",
          "Hidden persona management"
        ],
        deliverables: [
          { title: "Invisible Identity 2.0 Manual" },
          { title: "Complete Digital Invisibility Toolkit" }
        ],
        tech: ["ProtonMail", "NordVPN", "Brave", "Signal"]
      },
      {
        id: 10,
        title: "The 12-Month Plan: Building the Faceless Empire",
        transformation: "Build an annual plan to create and operate an empire of invisible businesses.",
        fullDescription: "Includes: quarter-by-quarter goals, progressive business creation, incremental automation, generating multiple income sources, total freedom from exposure.",
        skills: [
          "Strategic planning",
          "Digital empire creation",
          "Disciplined invisible execution"
        ],
        deliverables: [
          { title: "Complete Annual Plan" },
          { title: "12-Month Expansion Model" },
          { title: "Professional Invisible Operator Roadmap" }
        ],
        tech: ["Notion", "Google Sheets", "Trello", "Asana"]
      }
    ],
  },
];

export const getAcademyById = (id: string): Academy | undefined => {
  return academiesData.find((academy) => academy.id === id);
};
